/**
 * User Profile Component
 * 
 * Displays user information and provides logout functionality in the navigation bar
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslate } from '../../hooks/useTranslate';

const ZOOM_PRESETS = [50, 75, 80, 100, 120, 150];
const ZOOM_STORAGE_KEY = 'data-doctor-zoom';

function getStoredZoom(): number {
    try {
        const stored = localStorage.getItem(ZOOM_STORAGE_KEY);
        if (stored) {
            const val = Number(stored);
            if (ZOOM_PRESETS.includes(val)) return val;
        }
    } catch { /* ignore */ }
    return 80;
}

function applyZoom(percent: number) {
    document.documentElement.style.zoom = `${percent}%`;
    // Always set --app-zoom so .zoom-reset elements can counter it
    document.documentElement.style.setProperty('--app-zoom', String(percent / 100));
    // Only activate zoom-safe modal overrides when zoom > 100% (content grows, risk of overflow)
    if (percent > 100) {
        document.documentElement.setAttribute('data-zoom', String(percent));
    } else {
        document.documentElement.removeAttribute('data-zoom');
    }
}

export const UserProfile: React.FC = () => {
    const { user, logout, isLoading } = useAuth();
    const { t } = useTranslate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(getStoredZoom);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Apply stored zoom on mount
    useEffect(() => {
        applyZoom(zoomLevel);
    }, []);

    const handleZoomChange = useCallback((value: number) => {
        // Snap to nearest preset
        const snapped = ZOOM_PRESETS.reduce((prev, curr) =>
            Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
        );
        setZoomLevel(snapped);
        applyZoom(snapped);
        try { localStorage.setItem(ZOOM_STORAGE_KEY, String(snapped)); } catch { /* ignore */ }
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle logout
    const handleLogout = async () => {
        setIsDropdownOpen(false);
        await logout();
        // Navigation will be handled by App.tsx useEffect
    };

    if (!user) {
        return null;
    }

    // Get user initials for avatar
    const getInitials = (name: string): string => {
        return name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* User Button */}
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2 text-white hover:text-white/90 transition-colors group px-2 py-1 rounded-lg hover:bg-white/10"
                title={t('User Profile & Logout')}
                aria-label="User menu"
            >
                {/* Avatar with Initials Only */}
                <div className="h-10 w-10 rounded-lg flex items-center justify-center text-base font-medium text-white notranslate" style={{ backgroundColor: '#D31145' }}>
                    {getInitials(user.name)}
                </div>

                {/* Dropdown Arrow */}
                <svg
                    className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-50">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-700">
                        <div className="text-sm font-medium text-white notranslate">
                            {user.name}
                        </div>
                        <div className="text-xs text-gray-400 mt-1 notranslate">
                            {user.email}
                        </div>
                    </div>

                    {/* User Roles (if available) */}
                    {user.roles && user.roles.length > 0 && (
                        <div className="px-4 py-2 border-b border-gray-700">
                            <div className="text-xs text-gray-400 mb-1">{t('Roles')}</div>
                            <div className="flex flex-wrap gap-1">
                                {user.roles.map((role, index) => (
                                    <span
                                        key={index}
                                        className="px-2 py-1 bg-brand-600/20 text-brand-300 text-xs rounded"
                                    >
                                        {role}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Zoom / Scale Slider */}
                    <div className="px-4 py-3 border-b border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-gray-400">{t('Display Zoom')}</span>
                            <span className="text-xs font-semibold text-white">{zoomLevel}%</span>
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={ZOOM_PRESETS.length - 1}
                            step={1}
                            value={ZOOM_PRESETS.indexOf(zoomLevel)}
                            onChange={(e) => handleZoomChange(ZOOM_PRESETS[Number(e.target.value)])}
                            className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-red-500"
                            style={{ background: `linear-gradient(to right, #D31145 ${(ZOOM_PRESETS.indexOf(zoomLevel) / (ZOOM_PRESETS.length - 1)) * 100}%, #4B5563 ${(ZOOM_PRESETS.indexOf(zoomLevel) / (ZOOM_PRESETS.length - 1)) * 100}%)` }}
                            title={`${zoomLevel}%`}
                        />
                        <div className="flex justify-between mt-1">
                            {ZOOM_PRESETS.map((p) => (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => handleZoomChange(p)}
                                    className={`text-[10px] px-1 py-0.5 rounded transition-colors ${
                                        p === zoomLevel
                                            ? 'text-white font-bold bg-red-600/30'
                                            : 'text-gray-500 hover:text-gray-300'
                                    }`}
                                >
                                    {p}%
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                        {/* Logout */}
                        <button
                            onClick={handleLogout}
                            disabled={isLoading}
                            className={`
                                w-full text-left px-4 py-2 text-sm transition-colors
                                ${isLoading
                                    ? 'text-gray-500 cursor-not-allowed'
                                    : 'text-red-300 hover:bg-red-900/20 hover:text-red-200'
                                }
                            `}
                        >
                            <svg className="inline h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            {isLoading ? t('Signing out...') : t('Sign out')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserProfile;